import { ChangeDetectorRef, Component } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { DialogContentBase, DialogRef } from '@progress/kendo-angular-dialog';
import _ from 'lodash';
import { tap } from 'rxjs';
import { SchoolDetails } from 'src/app/modules/placement/models/placement-finder/school-list.model';
import { PlacementService } from 'src/app/modules/placement/services/placement.services';
import { LoadingService } from 'src/app/services/loading.service';
import { ReferenceService } from 'src/app/services/reference.service';
import {
  PLACEMENT_FILTER_CONSTANTS,
  PlacementFilterConstants,
} from '../../constants/placement-filters.constants';
import { ActionType } from '../../enums/action-type';
import { IReferenceData } from '../../interfaces/form/reference-data.model';
import { ISedmsFormOutput } from '../../interfaces/form/sedms-form-output.model';
import { PlacementFiltersReferenceData } from '../../interfaces/placement-filters-refernce';
import { StudentProfile } from '../../interfaces/student';
import { LiveRegionService } from 'src/app/shared/services/live-region.service';

@Component({
  selector: 'app-placement-filters',
  templateUrl: './placement-filters.component.html',
  styleUrls: ['./placement-filters.component.scss'],
  standalone: false,
})
export class PlacementFiltersComponent extends DialogContentBase {
  form: FormGroup;
  constants: PlacementFilterConstants = PLACEMENT_FILTER_CONSTANTS;
  studentDetails: StudentProfile;
  iepFormSk: string;
  referenceData: PlacementFiltersReferenceData;
  defaultFilters: SchoolDetails;
  previousFilters: SchoolDetails;
  isSummer: boolean = false;
  schoolYears: IReferenceData[] = [];
  currentAction: 'select' | 'deselect' | null = null;
  showNestedFields: boolean;
  programRatioList: IReferenceData[];
  schoolsList: any[] = [];
  currentFilters: SchoolDetails;
  readonly groupSiteTypes: string = 'siteTypes';
  programList: IReferenceData[];
  isReset: boolean = false;

  constructor(
    private fb: FormBuilder,
    dialog: DialogRef,
    private cdRef: ChangeDetectorRef,
    library: FaIconLibrary,
    private referenceService: ReferenceService,
    private loadingService: LoadingService,
    private placementService: PlacementService<ISedmsFormOutput>,
    private route: ActivatedRoute,
    private liveRegionService: LiveRegionService,
  ) {
    super(dialog);

    this.loadingService.show();

    this.referenceService
      .getDropDownValues('SERatio')
      .pipe(
        tap((data: IReferenceData[]) => {
          if (data) {
            this.programRatioList = [...data];

            const allPrograms: IReferenceData[] =
              PLACEMENT_FILTER_CONSTANTS.programList;
            if (this.studentDetails?.isPreSchool) {
              this.programList = allPrograms.filter(
                (p: IReferenceData) => p.value === 'SCIS' || p.value === 'SC',
              );
            } else {
              this.programList = allPrograms.filter(
                (p: IReferenceData) => p.value === 'ICT' || p.value === 'SC',
              );
            }
          }
        }),
      )
      .subscribe(() => {
        this.initializeForm(
          this.isReset
            ? {}
            : this.previousFilters
              ? this.previousFilters
              : this.defaultFilters,
        );
        this.isReset = false;
        this.currentFilters = this.previousFilters;
        this.loadingService.hide();
      });

    library.addIcons(faXmark);
  }

  initializeForm(filters): void {
    if (filters?.filters) {
      this.initializeFormValues(filters?.filters);
    } else {
      this.initializeFormValues(filters);
    }
  }

  initializeFormValues(filters: any): void {
    const selectedDistricts = Array.isArray(filters?.district)
      ? filters.district
      : [filters?.district].filter(Boolean);

    const selectedLanguages = Array.isArray(filters?.scClassLanguage)
      ? filters.scClassLanguage
      : [filters.scClassLanguage].filter(Boolean);

    const selectedPrograms = Array.isArray(filters?.scscis)
      ? filters.scscis
      : [filters.scscis].filter(Boolean);

    const selectedRatio = Array.isArray(filters?.scSpecialRatio)
      ? filters.scSpecialRatio
      : [filters.scSpecialRatio].filter(Boolean);

    this.form = this.fb.group({
      noSeat: [filters?.noSeat],
      showByAvailableSeats: [filters?.showByAvailableSeats],
      noSeats: [filters?.noSeats],
      varianceOne: [filters?.varianceOne],
      varianceTwo: [filters?.varianceTwo],
      isShowclassesforStudentLanguage: [
        filters?.isShowclassesforStudentLanguage,
      ],
      isAccessibleSchoolRequired: [filters?.isAccessibleSchoolRequired],
      restrictStudentsDistrict: [filters?.restrictStudentsDistrict],
      isShowClassesSupportStudentGrade: [filters?.isShowClassesSupportStudentGrade],
      language: [
        this.referenceData?.language
          .filter((lang: IReferenceData) =>
            selectedLanguages.includes(lang.description),
          )
          .map((lang: IReferenceData) => lang.id),
      ],
      programs: [
        this.programList.filter((p: IReferenceData) =>
          selectedPrograms.includes(p.value),
        ),
      ],
      ratio: [
        this.referenceData?.seRatio.filter((r: IReferenceData) =>
          selectedRatio.includes(r.description),
        ),
      ],

      preSchoolType: [
        this.referenceData?.preSchoolType.find(
          (p: IReferenceData) => p.description === filters?.preSchoolType,
        ) ?? null,
      ],
      placementRecType: [
        this.referenceData?.placementRecType.find(
          (p: IReferenceData) => p.description === filters?.placementRecType,
        ) ?? null,
      ],
      district: [
        this.referenceData?.district.filter((district: IReferenceData) =>
          selectedDistricts.includes(Number(district.id)),
        ),
      ],
      borough: [
        this.referenceData?.borough.find(
          (b: IReferenceData) =>
            b.description === filters?.borough || b.value === filters?.borough,
        ) ?? null,
      ],
      schoolYearTerm: [
        this.schoolYears.find(
          (y: IReferenceData) => y.value === filters?.schoolYear,
        ) ?? null,
      ],
    });

    this.showNestedFields = !!this.form.get('noSeat')?.value;

    this.form.get('noSeat')?.valueChanges.subscribe((value: boolean) => {
      this.showNestedFields = !!value;

      if (!value) {
        this.form.patchValue({
          showByAvailableSeats: false,
          noSeats: false,
          varianceOne: false,
          varianceTwo: false,
        });
      }
    });
  }

  public close(searchObj: any): void {
    this.dialog.close(searchObj);
  }

  toggleSelectAll(select: boolean): void {
    if (select && this.currentAction !== ActionType.Select) {
      this.onCheckboxChange(true, this.groupSiteTypes);
      this.currentAction = ActionType.Select;
    } else if (!select && this.currentAction !== ActionType.Deselect) {
      this.onCheckboxChange(false, this.groupSiteTypes);
      this.currentAction = ActionType.Deselect;
    }
  }

  onCheckboxChange(event: boolean, type?: string): void {
    const CONTROLS: string[] = _.map(this.constants[type], 'control');
    CONTROLS.forEach((ctrl: string) => {
      this.form.get(ctrl).patchValue(event);
    });
  }

  onShowresults(): void {
    this.liveRegionService.announceFiltersApplied();
    this.close({ schoolsList: this.schoolsList, filters: this.getSchoolsList });
  }

  get getSchoolsList(): SchoolDetails {
    const val = this.form.value;

    const searchObj: SchoolDetails = {
      scscis: val.programs?.length ? val.programs.map((p: any) => p.value) : [],
      scSpecialRatio: this.form?.value?.ratio?.length
        ? this.form.value.ratio.map((r: any) => r.value)
        : [],
      scClassLanguage: (val.language || [])
        .map((langId: number) => {
          const match: IReferenceData | undefined =
            this.referenceData.language.find(
              (ref: IReferenceData) => ref.id === langId,
            );
          return match ? match.description : null;
        })
        .filter(Boolean),
      noSeat: !!val.noSeat,
      showByAvailableSeats: !!val.showByAvailableSeats,
      noSeats: !!val.noSeats,
      varianceOne: !!val.varianceOne,
      varianceTwo: !!val.varianceTwo,
      isAccessibleSchoolRequired: val.isAccessibleSchoolRequired,
      restrictStudentsDistrict: !!val.restrictStudentsDistrict,
      isShowClassesSupportStudentGrade: val?.isShowClassesSupportStudentGrade,
      homeDistrict: Number(this.studentDetails?.homeDistrict),
      preSchoolType: val.preSchoolType?.description ?? null,
      placementRecType: val.placementRecType?.description ?? null,
      district: val?.district?.length ? val.district.map((d: any) => d.id) : [],
      borough: val.borough?.description ?? null,
      isSummer: this.isSummer,
      nycId: this.route.snapshot.params.nycId,
      schoolYear: val.schoolYearTerm?.value ?? null,
    } as SchoolDetails;
    this.loadingService.show();
    this.currentFilters = searchObj;
    return searchObj;
  }

  resetFilters(): void {
    this.isReset = false;
    this.previousFilters = null;
    this.initializeForm(this.defaultFilters);
    this.liveRegionService.announceFiltersCleared();
  }
}
